/**
 * Admin Tenant Search Component
 *
 * A search box for admin users to find and select tenants for support purposes.
 * When a tenant is selected, the app displays that tenant's data.
 *
 * This component is ONLY visible to users with ADMIN globalRole.
 *
 * @fileoverview Admin tenant search/impersonation component
 *
 * @example
 * // In Header actions slot (only renders for admins)
 * <Header
 *   actions={<AdminTenantSearch />}
 *   userMenu={<ProfileMenu />}
 * />
 */

import React, { useRef, useCallback } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  List,
  ListItem,
  Text,
  HStack,
  VStack,
  Badge,
  Spinner,
  Icon,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { MdExitToApp } from 'react-icons/md';

import { useUserProfile } from '../../hooks/useUserProfile';
import { useAdminTenantSearch } from '../../hooks/useAdminTenantSearch';
import { useUser } from '../../Context/userContext';
import type { TenantWithMembership } from '../../Context/userContext';
import { useSupportMode } from '../../shared/support-mode';
import { AdminTenant } from '../../types/admin';

/**
 * Props for AdminTenantSearch component
 */
interface AdminTenantSearchProps {
  /** Optional callback when tenant is selected */
  onTenantSelect?: (tenant: AdminTenant) => void;
  /** Optional callback when selection is cleared */
  onClear?: () => void;
}

/**
 * Convert AdminTenant to TenantWithMembership for context compatibility
 *
 * When an admin selects a tenant for support purposes, we need to
 * map the AdminTenant type to the format expected by userContext.
 * The admin is treated as an owner for display purposes.
 */
const mapToTenantWithMembership = (tenant: AdminTenant): TenantWithMembership => ({
  id: tenant.id,
  name: tenant.name,
  slug: tenant.slug,
  isActive: tenant.isActive,
  isOwner: false, // Admin is not the actual owner, just viewing
  createdAt: tenant.createdAt,
});

/**
 * Admin Tenant Search Component
 *
 * Features:
 * - Only renders for ADMIN users
 * - Search input with debounced API calls
 * - Dropdown showing matching tenants
 * - Each result shows: name, manager email, active status
 * - Clear button to reset selection
 * - Responsive: hidden on mobile, visible on md+ screens
 *
 * When a tenant is selected, the app's selectedTenant context is updated,
 * causing all data views to display that tenant's information.
 *
 * @param props - Component props
 * @returns Search component or null if not admin
 */
export const AdminTenantSearch: React.FC<AdminTenantSearchProps> = ({
  onTenantSelect,
  onClear,
}) => {
  const { isAdmin } = useUserProfile();
  const { selectTenant, clearSelectedTenant } = useUser();
  const { isInSupportMode, supportTenant, enterSupportMode, exitSupportMode } = useSupportMode();
  const {
    searchQuery,
    setSearchQuery,
    results,
    isLoading,
    error,
    clearSearch,
    hasResults,
  } = useAdminTenantSearch();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      setSearchQuery(value);
      if (value.length >= 2) {
        onOpen();
      } else {
        onClose();
      }
    },
    [setSearchQuery, onOpen, onClose]
  );

  /**
   * Handle tenant selection
   *
   * Updates the app's selectedTenant context so all data views
   * will show the selected tenant's information.
   * Also enters support mode to show the banner across all pages.
   */
  const handleSelectTenant = useCallback(
    (tenant: AdminTenant): void => {
      // Enter support mode (persists across pages)
      enterSupportMode(tenant);

      // Convert and update context so app shows tenant's data
      const tenantWithMembership = mapToTenantWithMembership(tenant);
      selectTenant(tenantWithMembership);

      // Clear search and close popover
      clearSearch();
      onClose();

      // Call optional callback
      onTenantSelect?.(tenant);
    },
    [enterSupportMode, selectTenant, clearSearch, onClose, onTenantSelect]
  );

  /**
   * Handle input focus
   */
  const handleFocus = useCallback((): void => {
    if (searchQuery.length >= 2) {
      onOpen();
    }
  }, [searchQuery, onOpen]);

  /**
   * Handle exiting support mode
   *
   * 1. Exit support mode context
   * 2. Clear selected tenant so the search box appears
   * 3. Call optional onClear callback
   */
  const handleExitSupportMode = useCallback((): void => {
    // Exit support mode
    exitSupportMode();

    // Clear selected tenant so admin can search for another tenant
    clearSelectedTenant();

    // Call optional callback
    onClear?.();
  }, [exitSupportMode, clearSelectedTenant, onClear]);

  // Only render for admin users
  if (!isAdmin) {
    return null;
  }

  // Show support mode indicator in header when in support mode
  if (isInSupportMode && supportTenant) {
    return (
      <Box display={{ base: 'none', md: 'block' }}>
        <HStack
          bg="purple.900"
          borderWidth="1px"
          borderColor="purple.500"
          borderRadius="md"
          px={3}
          py={1}
          spacing={3}
        >
          <Badge colorScheme="purple" variant="solid" fontSize="xs">
            Support Mode
          </Badge>
          <Text color="purple.100" fontSize="sm" fontWeight="medium" maxW="150px" isTruncated>
            {supportTenant.name}
          </Text>
          <Button
            size="xs"
            colorScheme="purple"
            variant="solid"
            leftIcon={<Icon as={MdExitToApp} />}
            onClick={handleExitSupportMode}
          >
            Exit
          </Button>
        </HStack>
      </Box>
    );
  }

  // Show search input
  return (
    <Box
      display={{ base: 'none', md: 'block' }}
      ref={popoverRef}
      position="relative"
    >
      <Popover
        isOpen={isOpen && (hasResults || isLoading || !!error)}
        onClose={onClose}
        placement="bottom-start"
        autoFocus={false}
        closeOnBlur={false}
      >
        <PopoverTrigger>
          <InputGroup size="sm" w="250px">
            <InputLeftElement>
              {isLoading ? (
                <Spinner size="xs" color="accent.primary" />
              ) : (
                <SearchIcon color="text.secondary" />
              )}
            </InputLeftElement>
            <Input
              ref={inputRef}
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleFocus}
              bg="bg.primary"
              borderColor="xacto.borderGray"
              color="text.primary"
              _placeholder={{ color: 'xacto.silverGray' }}
              _hover={{ borderColor: 'accent.primary' }}
              _focus={{
                borderColor: 'xacto.electricCyan',
                boxShadow: '0 0 0 1px var(--chakra-colors-xacto-electricCyan)',
              }}
            />
            {searchQuery && (
              <InputRightElement>
                <IconButton
                  aria-label="Clear search"
                  icon={<CloseIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={clearSearch}
                  color="text.secondary"
                  _hover={{ color: 'text.primary' }}
                />
              </InputRightElement>
            )}
          </InputGroup>
        </PopoverTrigger>

        <PopoverContent
          bg="bg.surface"
          borderColor="xacto.borderGray"
          w="300px"
          maxH="300px"
          overflow="auto"
          _focus={{ boxShadow: 'lg' }}
        >
          <PopoverBody p={0}>
            {/* Error state */}
            {error && (
              <Box p={3}>
                <Text color="red.400" fontSize="sm">
                  {error}
                </Text>
              </Box>
            )}

            {/* Loading state */}
            {isLoading && !error && (
              <Box p={3} textAlign="center">
                <Spinner size="sm" color="accent.primary" />
                <Text color="text.secondary" fontSize="sm" mt={2}>
                  Searching...
                </Text>
              </Box>
            )}

            {/* Results */}
            {!isLoading && !error && hasResults && (
              <List spacing={0}>
                {results.map((tenant) => (
                  <ListItem
                    key={tenant.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'bg.muted' }}
                    onClick={() => handleSelectTenant(tenant)}
                    borderBottomWidth="1px"
                    borderBottomColor="xacto.borderGray"
                    _last={{ borderBottom: 'none' }}
                  >
                    <VStack align="start" spacing={0}>
                      <HStack justify="space-between" w="full">
                        <Text
                          color="text.primary"
                          fontSize="sm"
                          fontWeight="medium"
                          noOfLines={1}
                        >
                          {tenant.name}
                        </Text>
                        <Badge
                          colorScheme={tenant.isActive ? 'green' : 'gray'}
                          fontSize="2xs"
                        >
                          {tenant.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </HStack>
                      {tenant.managerEmail && (
                        <Text color="text.secondary" fontSize="xs" noOfLines={1}>
                          {tenant.managerEmail}
                        </Text>
                      )}
                    </VStack>
                  </ListItem>
                ))}
              </List>
            )}

            {/* No results */}
            {!isLoading && !error && !hasResults && searchQuery.length >= 2 && (
              <Box p={3} textAlign="center">
                <Text color="text.secondary" fontSize="sm">
                  No tenants found
                </Text>
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default AdminTenantSearch;
